/**
 * Query-string construction.
 *
 * Mirrors: api/internal/httpapi/params.go — parsePage, parseSort, csvParam.
 *
 * Every filter in this app is a SERVER filter: it goes into the React Query key
 * AND into the query string, and the server applies it in SQL. Nothing here
 * exists to slice an array that was already fetched.
 */

/** A value that may appear in a query string. */
export type ParamValue = string | number | boolean | null | undefined | string[];

/**
 * Builds a URLSearchParams, dropping null/undefined/"" and joining arrays with
 * commas — httpapi.csvParam accepts both `?state=a&state=b` and `?state=a,b`.
 */
export function buildQuery(params: Record<string, ParamValue>): URLSearchParams {
  const search = new URLSearchParams();
  const keys = Object.keys(params).sort(); // stable, so the query key is stable
  for (const key of keys) {
    const value = params[key];
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      search.set(key, value.join(","));
      continue;
    }
    if (typeof value === "string" && value.trim() === "") continue;
    search.set(key, String(value));
  }
  return search;
}

/** `path?a=1&b=2`, or `path` when nothing survived. */
export function withQuery(path: string, params: Record<string, ParamValue>): string {
  const search = buildQuery(params).toString();
  return search === "" ? path : `${path}?${search}`;
}
